import { useRef, useState } from 'react';
import { useProjects } from '../../state/projects';
import { useSession } from '../../state/useSession';
import { assembleApp } from '../../api/build';
import { hostApp } from '../../api/hosting';
import { MAKE_PRIVATE_WARNING, TakenDownError, makePrivate, relistApp } from '../../api/publish';
import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import { Button } from '../../ui';

/**
 * Publishing is the point of the product: it ends in a URL you bookmark.
 *
 * The sequence mirrors the phone's publishWebEdit exactly — assemble and upload, then
 * prefer the stable saddle URL and fall back to the direct upload link — and the result
 * is written back onto the project so a reload still knows where the app lives.
 *
 * Every failure names its own reason. WebAppPublishError distinguishes five, and
 * collapsing them into "publishing failed" would hide the two the person can act on
 * (not signed in, no backend).
 */
export default function PublishPanel({ project }: { project: Project }) {
  const { user } = useSession();
  const definition = project.definition as AppDefinition;
  const [url, setUrl] = useState<string | null>(definition.webUrl ?? null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [takingDown, setTakingDown] = useState(false);
  const [wentPrivate, setWentPrivate] = useState(false);
  const [takenDown, setTakenDown] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  /**
   * Assemble on the server, then host. Shared by first publish and publish-again.
   *
   * Both steps carry the app's EXISTING id: it is the localStorage namespace baked into
   * the published document, so assembling or hosting under a fresh one would strand the
   * data of everyone already using the app.
   */
  async function publishContent(): Promise<void> {
    const appId = definition.hostedAppId ?? definition.id;
    const html = await assembleApp({
      appId,
      source: definition.source ?? '',
      appName: project.name,
      description: project.description,
    });
    const hosted = await hostApp({ appId, html });
    const live = hosted.siteUrl;
    await useProjects.getState().updateProject(
      project.id,
      { definition: { ...definition, webUrl: live, hostedAppId: appId } },
      user?.id ?? '',
    );
    setUrl(live ?? null);
  }

  async function publish(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await publishContent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not publish this app.');
    } finally {
      // Always: a stuck spinner is the failure people actually report.
      setBusy(false);
    }
  }

  /**
   * Put a private app back at the address it had.
   *
   * Two server steps, deliberately: relist restores the LISTING, and the page is still not
   * served until the content is republished. One button does both, because "publish it
   * again" is one intention — but it must not report success after only the first half.
   */
  async function publishAgain(): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await relistApp(definition.hostedAppId ?? '');
      await publishContent();
      setWentPrivate(false);
      setNotice('That app is public again, at the same address.');
    } catch (err) {
      if (err instanceof TakenDownError) {
        // Not a failure they can retry, so the retry affordance goes away with it.
        setTakenDown(true);
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'That app could not be published again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function copy(): Promise<void> {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard permission denied — the link is on screen and selectable anyway */
    }
  }

  /**
   * Take the app off the internet.
   *
   * The local record is updated ONLY after the server confirms, and never on failure: a
   * project that says "private" over a page that is still up is the exact bug this exists
   * to prevent, and it is worse than an error message.
   */
  async function takeDown(): Promise<void> {
    dialog.current?.close();
    setError(null);
    setNotice(null);
    setTakingDown(true);
    try {
      await makePrivate(definition.hostedAppId ?? '');
      const next = { ...definition };
      delete (next as { webUrl?: string }).webUrl;
      await useProjects.getState().updateProject(project.id, { definition: next }, user?.id ?? '');
      setUrl(null);
      setWentPrivate(true);
      setNotice('That app is private now. Only you can see its replies.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That app could not be taken down.');
    } finally {
      setTakingDown(false);
    }
  }

  return (
    <section className="publish-panel">
      <h2 className="type-micro section-label">Publish</h2>
      {url && (
        <p className="type-body publish-url">
          <a href={url} target="_blank" rel="noreferrer">{url}</a>
        </p>
      )}
      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
      {notice && <p role="status" className="type-caption">{notice}</p>}
      <div className="publish-actions">
        <Button
          title={url ? 'Publish changes' : 'Publish'}
          variant={url ? 'secondary' : 'primary'}
          onPress={() => void publish()}
          loading={busy}
        />
        {url && <Button title={copied ? 'Copied' : 'Copy link'} variant="ghost" onPress={() => void copy()} />}
      </div>

      {wentPrivate && !takenDown && (
        <Button title="Publish it again" onPress={() => void publishAgain()} loading={busy} />
      )}

      {url && (
        <Button
          title="Make private"
          variant="dangerSoft"
          loading={takingDown}
          onPress={() => dialog.current?.showModal()}
        />
      )}

      <dialog ref={dialog} aria-label="Make this app private" className="confirm-dialog">
        <h2 className="type-title">Make this app private?</h2>
        {MAKE_PRIVATE_WARNING.map((line) => (
          <p key={line} className="type-body">{line}</p>
        ))}
        <div className="build-actions">
          <Button title="Yes, take it offline" variant="dangerSoft" onPress={() => void takeDown()} />
          <Button title="Cancel" variant="ghost" onPress={() => dialog.current?.close()} />
        </div>
      </dialog>
    </section>
  );
}
